// Ops simulation Sadha T19 — the cashier's 'Offline bills' list.
//
// Rows come from the till's own queue (posDb.pending_sync), not from the
// server: a bill that never reached the server is exactly the one the cashier
// needs to see. A synced bill stays 24 h with its final (INV…) number.

import type { PendingSyncEntry } from '@/db/pos-database';
import { tenantNow } from '@/lib/tenant-clock';

export type OfflineBillStatus = 'PENDING' | 'SYNCING' | 'SYNCED' | 'FAILED' | 'NEEDS_REVIEW';

export interface OfflineBillRow {
  client_id: string;
  /** The till's number printed on the receipt (…-OFLnnnn). */
  offline_number: string;
  /** The server's number once synced (INV…), else null. */
  server_bill_number: string | null;
  /** Amount the customer paid, in the tenant's minor unit. */
  total: number;
  payment_method: string;
  customer_label: string | null;
  status: OfflineBillStatus;
  /** Why it is not synced yet, in plain words (null when nothing is wrong). */
  reason: string | null;
  created_at: string;
  synced_at: string | null;
  /** False while the device key that wrote it is not loaded (details unreadable). */
  readable: boolean;
}

export const OFFLINE_BILL_STATUS_CONFIG: Record<
  OfflineBillStatus,
  { label: string; tone: 'warning' | 'info' | 'success' | 'danger' | 'hold' }
> = {
  PENDING: { label: 'Pending', tone: 'warning' },
  SYNCING: { label: 'Syncing', tone: 'info' },
  SYNCED: { label: 'Synced', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'danger' },
  NEEDS_REVIEW: { label: 'Needs review', tone: 'hold' },
};

export const OFFLINE_BILL_STATUS_OPTIONS = (Object.keys(OFFLINE_BILL_STATUS_CONFIG) as OfflineBillStatus[]).map(
  (value) => ({ value, label: OFFLINE_BILL_STATUS_CONFIG[value].label }),
);

/** Synced bills stay in the list this long (owner decision). */
export const SYNCED_BILL_RETENTION_MS = 24 * 60 * 60 * 1000;

export function offlineBillStatus(status: PendingSyncEntry['status']): OfflineBillStatus {
  switch (status) {
    case 'PROCESSING':
      return 'SYNCING';
    case 'SYNCED':
      return 'SYNCED';
    case 'CONFLICT':
      return 'NEEDS_REVIEW';
    case 'FAILED':
    case 'DEAD':
      return 'FAILED';
    default:
      return 'PENDING';
  }
}

/** Server codes a refused bill comes back with, as a cashier would say them. */
const REASON_CODES: Record<string, string> = {
  INSUFFICIENT_STOCK: 'Not enough stock on the server for an item',
  BILL_VALIDATION_FAILED: 'The server could not accept the bill — a price or product may have changed',
  BILL_DISCOUNT_FAILED: 'The bill discount could not be applied on the server',
  STORE_CREDIT_INSUFFICIENT: 'Store credit insufficient',
  LOYALTY_INSUFFICIENT: 'Loyalty points insufficient',
  LOYALTY_BELOW_MINIMUM: 'Loyalty points below the redemption minimum',
};

const NETWORK_ERROR = /failed to fetch|network|load failed|timeout|timed out|offline|ECONN|abort/i;

/**
 * The reason a bill is not synced, in plain words. Empty for a bill that is
 * pending or synced — those need nothing from the cashier.
 */
export function plainSyncReason(entry: Pick<PendingSyncEntry, 'status' | 'last_error'>): string | null {
  if (entry.status === 'PENDING' || entry.status === 'PROCESSING' || entry.status === 'SYNCED') {
    return null;
  }
  const raw = (entry.last_error ?? '').replace(/^Max retries exceeded\. Last error: /, '').trim();
  if (!raw) {
    return entry.status === 'CONFLICT' ? 'Waiting for a manager to review it' : 'Could not sync — try again';
  }
  if (REASON_CODES[raw]) return REASON_CODES[raw];
  if (NETWORK_ERROR.test(raw)) return 'Could not reach the server — it will try again';
  return raw;
}

/** A queue row as a list row. `entry` must already be decrypted (or still sealed). */
export function toOfflineBillRow(entry: PendingSyncEntry): OfflineBillRow {
  const payload = (entry.payload ?? {}) as Record<string, unknown>;
  const readable = typeof payload.bill_number === 'string';
  return {
    client_id: entry.client_id,
    offline_number: readable ? String(payload.bill_number) : entry.client_id.slice(0, 8),
    server_bill_number: entry.server_bill_number ?? null,
    total: Number(payload.amount_paid ?? payload.grand_total ?? 0),
    payment_method: typeof payload.payment_method === 'string' ? payload.payment_method : '',
    customer_label: entry.customer_label ?? null,
    status: offlineBillStatus(entry.status),
    reason: plainSyncReason(entry),
    created_at: typeof payload.created_at === 'string' ? payload.created_at : entry.created_at,
    synced_at: entry.synced_at ?? null,
    readable,
  };
}

export interface OfflineBillFilters {
  /** Empty = every status. */
  statuses: OfflineBillStatus[];
  /** '' = every payment method. */
  paymentMethod: string;
  /** Inclusive local-day range on the sale time; null = any day. */
  dateRange: { from: Date; to: Date } | null;
  /** OFL / INV number or customer, any part, any case. */
  search: string;
}

export const EMPTY_OFFLINE_BILL_FILTERS: OfflineBillFilters = {
  statuses: [],
  paymentMethod: '',
  dateRange: null,
  search: '',
};

/** Calendar day of a Date's local fields, as a sortable number (20261024). */
function dayKey(d: Date): number {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

/**
 * `dateRange` holds store-calendar dates (T18 `storeDatePresets` / the
 * DatePicker), so each sale time is read on the store's clock before it is
 * compared — never the device's.
 */
export function filterOfflineBills(
  rows: OfflineBillRow[],
  f: OfflineBillFilters,
  toStoreClock: (instant: Date) => Date = tenantNow,
): OfflineBillRow[] {
  const needle = f.search.trim().toLowerCase();
  const from = f.dateRange ? dayKey(f.dateRange.from) : null;
  const to = f.dateRange ? dayKey(f.dateRange.to) : null;
  return rows.filter((row) => {
    if (f.statuses.length > 0 && !f.statuses.includes(row.status)) return false;
    if (f.paymentMethod && row.payment_method !== f.paymentMethod) return false;
    if (from !== null && to !== null) {
      const at = new Date(row.created_at);
      if (Number.isNaN(at.getTime())) return false;
      const day = dayKey(toStoreClock(at));
      if (day < from || day > to) return false;
    }
    if (needle) {
      const haystack = [row.offline_number, row.server_bill_number, row.customer_label]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

export function hasOfflineBillFilters(f: OfflineBillFilters): boolean {
  return f.statuses.length > 0 || !!f.paymentMethod || !!f.dateRange || !!f.search.trim();
}
